import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartUpdateEvent } from '@theme/events';

let pendingCartRequest = null;
/**
 * Last known cart, shared by every updater on the page. Refreshed by every cart fetch and every
 * `cartUpdate` event, so a quantity change can plan its delta and its FS split without a blocking
 * `/cart.js` round trip first. A stale snapshot is detected after the write and retried.
 * @type {{ items: any[] } | null}
 */
let cachedCart = null;
const GENERATED_PROPERTY_KEY = /^\d+$/;
const BACKORDER_TYPE = 'Bestillingsvare';

/** @param {Record<string, string> | null | undefined} properties */
function sanitizeLineProperties(properties) {
  return Object.fromEntries(Object.entries(properties || {}).filter(([key]) => !GENERATED_PROPERTY_KEY.test(key)));
}

export class ProductQuantityUpdater extends Component {
  #debouncedUpdate = debounce(this.#updateCart, 300).bind(this);
  #isUpdating = false;
  #currentQuantity = 0;

  connectedCallback() {
    super.connectedCallback();
    // Listen for quantity selector updates (event bubbles from input)
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    // Defer cart fetch until user interacts with the input to avoid unnecessary page load fetches
    const input = this.querySelector('input');
    if (input) {
      input.addEventListener('focus', this.#loadInitialQuantityOnce, { once: true });
      input.addEventListener('pointerenter', this.#loadInitialQuantityOnce, { once: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  async #loadInitialQuantity() {
    try {
      const cart = await this.#fetchCart();
      if (cart) {
        this.#syncQuantityFromCart(cart);
      }
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  #hasLoadedInitialQuantity = false;

  #loadInitialQuantityOnce = () => {
    if (this.#hasLoadedInitialQuantity) return;
    this.#hasLoadedInitialQuantity = true;
    this.#loadInitialQuantity();
  };

  #handleQuantityChange = (event) => {
    // Prevent updates while we're already updating
    if (this.#isUpdating) return;
    this.#debouncedUpdate(event);
  };

  async #updateCart(event) {
    // The snapshot can be behind the server (another tab, an app), which would make the delta wrong.
    // One retry against a freshly fetched cart covers that without paying for a fetch every time.
    if ((await this.#applyQuantity(event, true)) === 'stale') {
      await this.#applyQuantity(event, false);
    }
  }

  /**
   * @param {{ detail: { quantity: number } }} event
   * @param {boolean} allowCachedCart
   * @returns {Promise<'ok' | 'stale'>} `stale` when the snapshot was used and the write missed the target
   */
  async #applyQuantity(event, allowCachedCart) {
    const { quantity } = event.detail;
    const variantId = this.dataset.variantId;

    if (!variantId) return 'ok';

    const targetQuantity = Number(quantity);
    if (Number.isNaN(targetQuantity)) return 'ok';

    // No-op if value hasn't changed
    if (targetQuantity === this.#currentQuantity) return 'ok';

    if (this.#currentQuantity === 0 && targetQuantity === 0) return 'ok';

    this.#isUpdating = true;
    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    const sections = this.#getSectionsToUpdate();

    try {
      const usedCachedCart = allowCachedCart && cachedCart != null;
      const cartBefore = usedCachedCart ? cachedCart : await this.#fetchCart();
      if (!cartBefore) throw new Error('Cart state unavailable');

      const matchingItems = this.#getCartItems(cartBefore, variantId);
      const currentQuantity = matchingItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      const delta = targetQuantity - currentQuantity;
      if (delta === 0) return 'ok';

      const properties = {};
      for (const input of this.querySelectorAll('input[name^="properties["]')) {
        const match = input.name.match(/properties\[(.+)\]/);
        if (match) properties[match[1]] = input.value;
      }
      const sanitizedProperties = sanitizeLineProperties(properties);

      /** @type {{ items: any[], sections?: any } | null} */
      let cart = null;
      let response;

      if (delta > 0) {
        const additions = this.#planAdditions(cartBefore, delta, sanitizedProperties);
        const payload = {
          items: additions.map((addition) => ({
            id: variantId,
            quantity: addition.quantity,
            properties: addition.properties,
          })),
          sections: sections.join(','),
          sections_url: window.location.pathname,
        };
        response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      } else {
        let remaining = Math.abs(delta);
        const updates = {};
        const removalOrder = [...matchingItems].sort((left, right) => {
          const leftBackorder = left.properties?.type === BACKORDER_TYPE ? 1 : 0;
          const rightBackorder = right.properties?.type === BACKORDER_TYPE ? 1 : 0;
          return rightBackorder - leftBackorder;
        });

        for (const item of removalOrder) {
          if (remaining <= 0) break;
          const quantity = Number(item.quantity) || 0;
          const removed = Math.min(quantity, remaining);
          updates[item.key] = quantity - removed;
          remaining -= removed;
        }

        const payload = {
          updates,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        };
        response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', { body: JSON.stringify(payload) }));
      }

      if (!response.ok) throw await this.#cartError(response);
      const responseData = await response.json();

      // `/cart/update.js` answers with the whole cart, so the decrement path needs no follow-up fetch.
      // `/cart/add.js` answers with the added lines only, so there we still have to fetch.
      if (Array.isArray(responseData?.items) && responseData.item_count != null) {
        cart = responseData;
        cachedCart = cart;
      } else {
        cart = await this.#fetchCart();
      }

      if (cart) {
        this.#syncQuantityFromCart(cart);
      }

      document.dispatchEvent(
        new CartUpdateEvent(cart, this.id, {
          source: 'product-quantity-updater',
          variantId,
          itemCount: cart?.item_count,
          sections: responseData?.sections,
        }),
      );

      if (!usedCachedCart || !cart) return 'ok';

      const finalQuantity = this.#getCartItems(cart, variantId).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
      return finalQuantity === targetQuantity ? 'ok' : 'stale';
    } catch (error) {
      console.error('Error updating cart:', error);
      // Reload cart state on error
      cachedCart = null;
      this.#loadInitialQuantity();
      return 'ok';
    } finally {
      this.#isUpdating = false;
      this.classList.remove('is-loading');
      this.removeAttribute('aria-busy');
    }
  }

  #getSectionsToUpdate() {
    const sections = [];
    document.querySelectorAll('cart-items-component, cart-icon-component, cart-drawer-component').forEach((element) => {
      if (element.dataset.sectionId) {
        sections.push(element.dataset.sectionId);
      }
    });
    return sections;
  }

  /**
   * Splits an FS or DE addition across the in-stock and Bestillingsvare lines up front, so the cart
   * never renders an allocation the reconciler has to correct afterwards.
   *
   * A broached styk is never pre-split: the vendor does not supply loose units, so the whole quantity
   * stays unmarked and the cart offers the shopper a whole kolli instead.
   * @returns {{ quantity: number, properties: Record<string, string> }[]}
   */
  #planAdditions(cart, delta, properties) {
    const container = this.closest('.buy-buttons-block');
    const stock = Number(container?.dataset.inventoryQuantity);
    const isColli = this.dataset.productType === 'Bundle/Colli';

    const assortment = container instanceof HTMLElement ? container.dataset.assortment : '';
    const splitsOnStock = assortment === 'FS' || assortment === 'DE';
    const isBroachedUnit = container instanceof HTMLElement && container.dataset.singleUnitMode === 'shelf_capped' && !isColli;

    if (!(container instanceof HTMLElement) || !splitsOnStock || isBroachedUnit || properties.type === BACKORDER_TYPE || !Number.isFinite(stock)) {
      return [{ quantity: delta, properties }];
    }

    const unitsPerColli = Math.max(1, Number(container.dataset.unitsPerColli) || 1);
    const factor = isColli ? unitsPerColli : 1;
    const reservedUnits = this.#regularQuantity(cart, container.dataset.unitVariantId) + this.#regularQuantity(cart, container.dataset.colliVariantId) * unitsPerColli;

    const capacity = Math.max(0, Math.floor((stock - reservedUnits) / factor));
    const regular = Math.min(delta, capacity);
    const backorder = delta - regular;

    const additions = [];
    if (backorder > 0) additions.push({ quantity: backorder, properties: { ...properties, type: BACKORDER_TYPE } });
    if (regular > 0) additions.push({ quantity: regular, properties });
    return additions.length ? additions : [{ quantity: delta, properties }];
  }

  #regularQuantity(cart, variantId) {
    return this.#getCartItems(cart, variantId)
      .filter((item) => item.properties?.type !== BACKORDER_TYPE)
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  async #cartError(response) {
    let message = response.statusText || 'Unknown cart error';
    try {
      const error = await response.json();
      message = error.description || error.errors || error.message || JSON.stringify(error);
    } catch {
      // Keep the HTTP status text when Shopify doesn't return JSON.
    }
    return new Error(`Failed to update cart (${response.status}): ${message}`);
  }

  #syncQuantityFromCart(cart) {
    const myVariantId = Number(this.dataset.variantId);
    if (!myVariantId) return;

    const items = this.#getCartItems(cart, myVariantId);
    const newQuantity = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    this.#currentQuantity = newQuantity;

    const input = this.querySelector('input');
    if (input && Number(input.value) !== newQuantity) {
      if (document.activeElement !== input) {
        input.value = newQuantity;
      }
    }

    this.#updateSharedInventoryLimits(cart);
  }

  #updateSharedInventoryLimits(cart) {
    const container = this.closest('.buy-buttons-block');
    if (!(container instanceof HTMLElement)) return;

    const assortment = container.dataset.assortment;
    const isBroached = container.dataset.singleUnitMode === 'shelf_capped';
    if (assortment !== 'US' && !isBroached) return;

    const inventoryQuantity = Number(container.dataset.inventoryQuantity || 0);
    const unitsPerColli = Number(container.dataset.unitsPerColli || 0);
    if (!Number.isFinite(inventoryQuantity) || !Number.isFinite(unitsPerColli) || unitsPerColli <= 0) return;

    const unitVariantId = container.dataset.unitVariantId;
    const colliVariantId = container.dataset.colliVariantId;

    if (assortment === 'US') {
      const unitInCart = this.#getCartItemQuantity(cart, unitVariantId);
      const colliInCart = this.#getCartItemQuantity(cart, colliVariantId);

      const unitMax = Math.max(inventoryQuantity - colliInCart * unitsPerColli, 0);
      const colliMax = Math.max(Math.floor((inventoryQuantity - unitInCart) / unitsPerColli), 0);

      this.#setVariantMax(container, unitVariantId, unitMax);
      this.#setVariantMax(container, colliVariantId, colliMax);
      return;
    }

    /* Theme change owner: Novicell — recomputes the broached styk ceiling after every cart change;
       why: the shelf is shared, so a kolli already covered by store stock has reserved its units and
       the styk input has to drop by that much. Mirrors get-unit-max-quantity.liquid, which renders the
       same number server-side. Kolli itself stays unlimited, because kolli can always be backordered. */
    const colliCovered = Math.min(this.#regularQuantity(cart, colliVariantId), Math.floor(inventoryQuantity / unitsPerColli));
    const unitMax = Math.max(inventoryQuantity - colliCovered * unitsPerColli, 0);

    this.#setVariantMax(container, unitVariantId, unitMax);
  }

  #getCartItemQuantity(cart, variantId) {
    if (!variantId) return 0;
    const numericId = Number(variantId);
    if (!numericId) return 0;

    return this.#getCartItems(cart, numericId).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  }

  #getCartItems(cart, variantId) {
    const numericId = Number(variantId);
    if (!numericId) return [];

    return (cart.items || []).filter((item) => Number(item.id) === numericId || Number(item.variant_id) === numericId);
  }

  #setVariantMax(container, variantId, maxValue) {
    if (!variantId) return;
    const updater = container.querySelector(`product-quantity-updater[data-variant-id="${variantId}"]`);
    if (!(updater instanceof HTMLElement)) return;

    const input = updater.querySelector('input[type="number"]');
    if (!(input instanceof HTMLInputElement)) return;

    input.max = String(maxValue);
  }

  #onCartUpdate = (event) => {
    // Avoid infinite loop if this component triggered the update
    if (event.detail.sourceId === this.id) return;

    // Mark as loaded since we're receiving cart data from elsewhere
    this.#hasLoadedInitialQuantity = true;

    // The cart payload might not always be present; fetch to stay in sync
    const cart = event.detail.resource;
    if (Array.isArray(cart?.items)) {
      cachedCart = cart;
      this.#syncQuantityFromCart(cart);
      return;
    }

    // The cart changed but we were not handed the new state, so the snapshot cannot be trusted.
    // `pendingCartRequest` collapses this into one request for every updater on the page.
    cachedCart = null;
    void this.#fetchCart().then((freshCart) => {
      if (freshCart) {
        this.#syncQuantityFromCart(freshCart);
      }
    });
  };

  async #fetchCart() {
    if (pendingCartRequest) {
      return pendingCartRequest;
    }

    pendingCartRequest = fetch(`${Theme.routes.cart_url}.js`)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch cart (${response.status})`);
        }
        cachedCart = await response.json();
        return cachedCart;
      })
      .catch((error) => {
        console.error('Error fetching cart state:', error);
        return null;
      })
      .finally(() => {
        pendingCartRequest = null;
      });

    return pendingCartRequest;
  }
}

if (!customElements.get('product-quantity-updater')) {
  customElements.define('product-quantity-updater', ProductQuantityUpdater);
}
